'use client';

import { normalizePrintSizeValue } from '@repo/artwork-generation/print-ratio-catalog';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Info, X } from 'lucide-react';
import { getFrameNameKey } from '@/app/artworks/[id]/print/utils/frameNames';
import type { AspectRatioOrientation } from '@/components/creation-workspace/AspectRatioOrientationTabs';
import { formatPrintSizeLabel } from '@/components/creation-workspace/placeholder-data';
import { usePrintOptions } from '@/hooks/usePrintOptions';

interface CompatiblePrintSizesProps {
  aspectRatio: string;
  orientation: AspectRatioOrientation;
  printSizes: string[];
  compact: boolean;
}

export function CompatiblePrintSizes({
  aspectRatio,
  orientation,
  printSizes,
  compact,
}: CompatiblePrintSizesProps) {
  const t = useTranslations('create.sizeAndQualityModal.slider.frameAvailability');
  const frameT = useTranslations('artworkPrint.frameNames.full');
  // Keep each orientation's catalog mounted so switching shapes does not clear
  // availability and briefly remove limited-frame indicators while refetching.
  const portraitOptions = usePrintOptions({
    aspectRatioWidth: 2,
    aspectRatioHeight: 3,
  });
  const squareOptions = usePrintOptions({
    aspectRatioWidth: 1,
    aspectRatioHeight: 1,
  });
  const landscapeOptions = usePrintOptions({
    aspectRatioWidth: 3,
    aspectRatioHeight: 2,
  });
  const { sizes, frames } = {
    portrait: portraitOptions,
    square: squareOptions,
    landscape: landscapeOptions,
  }[orientation];
  const [expandedSize, setExpandedSize] = useState<string | null>(null);
  const expandedTriggerRef = useRef<HTMLButtonElement>(null);
  const detailsId = useId();

  useEffect(() => {
    setExpandedSize(null);
  }, [aspectRatio]);

  const sizeOptions = printSizes.map(size => {
    const printOption = sizes.find(
      option => normalizePrintSizeValue(option.dimensions) === normalizePrintSizeValue(size),
    );
    const frameVariants = printOption?.frameVariants;
    const supportedFrames = frameVariants
      ? frames.filter(frame => frame.name in frameVariants)
      : [];

    return {
      size,
      label: formatPrintSizeLabel(size).replace(' x ', ' × '),
      supportedFrames,
      limited: supportedFrames.length > 0 && supportedFrames.length < frames.length,
    };
  });
  const expandedOption = sizeOptions.find(option => option.size === expandedSize && option.limited);
  const hasLimitedSizes = sizeOptions.some(option => option.limited);
  const pillClassName =
    'relative inline-flex items-center justify-center rounded-full border bg-white/80 px-2 py-0.5 text-[10px] font-medium leading-4 text-gray-700 dark:bg-slate-900/80 dark:text-slate-200';

  return (
    <div className="mt-2">
      <div
        className={`grid grid-cols-[repeat(auto-fill,minmax(5rem,1fr))] gap-1.5 ${compact ? 'mx-auto max-w-72' : 'max-w-56'}`}
      >
        {sizeOptions.map(option =>
          option.limited ? (
            <button
              key={option.size}
              type="button"
              ref={expandedOption?.size === option.size ? expandedTriggerRef : undefined}
              title={t('limited')}
              aria-label={t('showFrames', { size: option.label })}
              aria-expanded={expandedOption?.size === option.size}
              aria-controls={expandedOption?.size === option.size ? detailsId : undefined}
              onClick={() =>
                setExpandedSize(current => (current === option.size ? null : option.size))
              }
              className={`${pillClassName} cursor-pointer border-amber-300/70 hover:border-amber-400 dark:border-amber-500/40 dark:hover:border-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400`}
            >
              <span>{option.label}</span>
              <Info
                aria-hidden="true"
                className="absolute right-1 top-1/2 size-3 -translate-y-1/2 text-amber-700 dark:text-amber-300"
              />
            </button>
          ) : (
            <span
              key={option.size}
              className={`${pillClassName} cursor-default border-gray-200 dark:border-slate-700`}
            >
              <span>{option.label}</span>
            </span>
          ),
        )}
      </div>
      <p
        aria-hidden={!hasLimitedSizes}
        className={`mt-2 flex items-start gap-1.5 text-[11px] leading-4 text-gray-500 dark:text-slate-400 ${compact ? 'justify-center' : 'max-w-56'} ${hasLimitedSizes ? '' : 'invisible'}`}
      >
        <Info
          aria-hidden="true"
          className="mt-0.5 size-3 shrink-0 text-amber-700 dark:text-amber-300"
        />
        <span>{t('note')}</span>
      </p>
      {expandedOption && (
        <div
          id={detailsId}
          role="region"
          aria-label={t('availableForSize', { size: expandedOption.label })}
          className="relative mt-2 rounded-lg border border-gray-200 bg-white/90 p-2 pr-7 text-left text-[11px] leading-relaxed dark:border-slate-700 dark:bg-slate-900/90"
        >
          <p className="font-medium text-gray-500 dark:text-slate-400">
            {t('availableForSize', { size: expandedOption.label })}
          </p>
          <ul className="mt-1 text-gray-700 dark:text-slate-200">
            {expandedOption.supportedFrames.map(frame => {
              const frameKey = getFrameNameKey(frame.name);
              return <li key={frame.id}>{frameKey ? frameT(frameKey) : frame.name}</li>;
            })}
          </ul>
          <button
            type="button"
            onClick={() => {
              expandedTriggerRef.current?.focus();
              setExpandedSize(null);
            }}
            aria-label={t('close')}
            className="absolute right-0.5 top-0.5 flex size-6 items-center justify-center rounded text-gray-500 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 dark:text-slate-400 dark:hover:text-slate-100"
          >
            <X className="size-3" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}
